// import { fooServiceModule } from '../../services/foo/foo-service';

export const bufferedSaveHelperModule = angular
  .module('Q.helpers.buffered-save-helper', [
    // fooServiceModule,
  ])

  .factory('bufferedSaveHelper', function ($timeout, $q) {
    const AUTOSAVE_RATE = 500;

    /**
     * This function creates a function that can be used to safely save changes repeatedly. These changes
     * are buffered, so we don't make more than one request per maxRate, unless there's a tag (specifying
     * it's an important update).
     *
     * Each call to the generated function returns a Promise, which is guaranteed to resolve or reject eventually
     * (assuming the underlying saveFn resolves), even if it's delayed (due to grouping saves) or buffered until
     * an existing save completes.
     *
     * @TODO One issue with this design is we always save the current model at the time the current save completes,
     *       rather than caching the save at that time.
     *
     * @param {Function} saveFn The function that performs the actual save. Will be passed in a tag if provided.
     * @param {number} maxRate Override default max rate to make saves
     * @returns {Function} Function that will only save on regular intervals
     */
    return function bufferedSaveHelper(saveFn, maxRate = AUTOSAVE_RATE) {
      let delay = null;
      let saving = false;
      // stores a deferred that's resolved after all delayed and waiting saves are completed
      let savingDeferred;

      // these are saves waiting on the delay to time out
      let delayedSaves = [];

      // these are saves that need to be triggered after the current save completes
      let waitingSaves = [];

      // used to resolve or reject any delayed save deferreds
      const notifyDelayedSaves = (type) => (resp) => {
        delayedSaves.forEach((d) => d[type](resp));
        delayedSaves = [];
        return $q[type](resp);
      };

      // This method is supposed to only enable triggering autosaves every 500ms or so
      // and other saves immediately. The design needs to enable listening to the returned promise,
      // which should always be resolved or rejected once we've actually handled a save, even if that save was
      // triggered from somewhere else.
      // There's also the oddity of needing a secondary workflow for triggering a save as soon as we return
      // from the previous save attempt.
      function bufferedSave(tag) {
        if (saving) {
          // we can't re-save until we know the previous save was successful
          // store the tag for future use, as well as a deferred for this save
          waitingSaves.tag = waitingSaves.tag || tag;
          const d = $q.defer();
          waitingSaves.push(d);
          return d.promise;
        }

        if (delay) {
          // clear any previous delay (but retain the delayedSaves)
          $timeout.cancel(delay);
          delay = null;
        }

        // initialize a new afterSave state if necessary
        savingDeferred = savingDeferred || $q.defer();
        bufferedSave.afterSave = savingDeferred.promise;

        function _saveNow(tag) {
          saving = true;
          delay = null;
          return saveFn(tag)
            .then(notifyDelayedSaves('resolve'), notifyDelayedSaves('reject'))
            .finally(() => {
              saving = false;
              if (waitingSaves.length) {
                // if we have any waiting saves, convert them to delayed saves, and re-save
                const tag = waitingSaves.tag;
                delayedSaves = waitingSaves;
                waitingSaves = [];
                // if this is a tagged save, it'll get triggered immediately
                bufferedSave(tag);
              } else {
                savingDeferred.resolve();
                savingDeferred = null;
              }
            });
        }

        if (tag) {
          // trigger tagged saves immediately
          return _saveNow(tag);
        } else {
          // delay at least 500ms before starting autosaves (which will continue to stretch out until
          // input stops, or we get a tagged save)
          const d = $q.defer();
          delayedSaves.push(d);
          delay = $timeout(_saveNow, maxRate);
          return d.promise;
        }
      }

      // returns true if we're saving or waiting on a save
      bufferedSave.isSaving = () =>
        !!(saving || delayedSaves.length || waitingSaves.length);
      // initialize afterSave to resolve immediately
      bufferedSave.afterSave = $q.resolve();

      return bufferedSave;
    };
  });
